import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@shared/api'
import { toast } from '@shared/lib'

export const useApplicationStore = defineStore('application', () => {
  const application = ref({})
  const entranceExamDisciplines = ref([])
  const entranceExamErrors = ref({})
  const scanCategories = ref([])
  const loading = ref(false)

  //secretary
  const fetchForSecretary = async (formId, applicationId, silent = false) => {
    if (!silent) loading.value = true
    try {
      const { data } = await api.get(`/api/v1/secretary/forms/${formId}/applications/${applicationId}`)
      application.value = data.application
      entranceExamDisciplines.value = data.entrance_exam_disciplines
      scanCategories.value = data.scan_categories
    } catch {
      if (silent) toast.error('Не удалось обновить состояние заявления')
    } finally {
      if (!silent) loading.value = false
    }
  }

  //abiturient
  const fetchForAbiturient = async (id, silent = false) => {
    if (!silent) loading.value = true
    try {
      const { data } = await api.get(`/api/v1/abiturient/applications/${id}/edit`)
      application.value = data.application
      entranceExamDisciplines.value = data.entrance_exam_disciplines
      scanCategories.value = data.scan_categories
    } catch {
      if (silent) toast.error('Не удалось обновить состояние заявления')
    } finally {
      if (!silent) loading.value = false
    }
  }

  const uploadScan = async (file, category) => {
    try {
      const formData = new FormData()
      formData.append('application[scans_attributes][0][file]', file)
      formData.append('application[scans_attributes][0][category]', category)
      await api.patch(
        `/api/v1/abiturient/applications/${application.value.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
    } catch (error) {
      console.error('uploadScan failed', error)
    }
  }

  const updateEntranceExam = async (examId, scheduleId) => {
    entranceExamErrors.value[examId] = []

    try {
      await api.patch(
        `/api/v1/abiturient/applications/${application.value.id}/entrance_exams/${examId}`,
        { entrance_exam: { schedule_id: scheduleId } }
      )
      await fetchForAbiturient(application.value.id, true)
      toast.success('Запись на испытание обновлена')
    } catch (error) {
      entranceExamErrors.value[examId] = error.response?.data?.errors?.entrance_exam?.base ?? []
      toast.error('Не удалось обновить запись на испытание')
    }
  }

  const getChangeHistory = async (applicationId = application.value.id) => {
    if (!applicationId) return []

    try {
      const { data } = await api.get(`/api/v1/admin/applications/${applicationId}/changelog`)
      return data.changelog ?? []
    } catch {
      toast.error('Не удалось загрузить список изменений')
      return []
    }
  }

  const clearEntranceExamError = (examId) => {
    entranceExamErrors.value[examId] = []
  }

  const destroyScan = async (scanId) => {
    try {
      const formData = new FormData()
      formData.append('application[scans_attributes][0][id]', scanId)
      formData.append('application[scans_attributes][0][_destroy]', true)
      await api.patch(
        `/api/v1/abiturient/applications/${application.value.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
    } catch (error) {
      console.error('destroyScan failed', error)
    }
  }

  //Contracts
  const contracts = ref([])
  const payContestGroups = ref([])
  const contractInfoKinds = ref([])
  const contractCustomerKinds = ref([])
  const contractTypes = ref([])
  const contractPaymentTypeKinds = ref([])
  const paymentTypes = ref([])

  const fetchContracts = async (formId, role = 'abiturient') => {
    const url = {
      abiturient: `/api/v1/abiturient/applications/${application.value.id}/contracts`,
      secretary: `/api/v1/secretary/forms/${formId}/applications/${application.value.id}/contracts`
    }
    try {
      const { data } = await api.get(url[role])
      contracts.value = data.contracts ?? []
      payContestGroups.value = data.pay_contest_groups ?? []
      contractInfoKinds.value = data.contract_info_kinds ?? []
      contractCustomerKinds.value = data.contract_customer_kinds ?? []
      contractTypes.value = data.contract_types ?? []
      contractPaymentTypeKinds.value = data.contract_payment_types ?? []
      paymentTypes.value = data.payment_types ?? []
    } catch {
      toast.error('Не удалось загрузить договоры')
    }
  }

  const notifyContract = async (contractId) => {
    try {
      await api.post(`/api/v1/abiturient/applications/${application.value.id}/contracts/${contractId}/notify`)
      await fetchContracts()
      toast.success('Заявка отправлена!')
    } catch {
      toast.error('Не удалось отправить заявку')
    }
  }

  const createContract = async (contractAttrs) => {
    await api.post(
      `/api/v1/abiturient/applications/${application.value.id}/contracts`,
      { contract: { ...contractAttrs, application_id: application.value.id } }
    )
    await fetchContracts()
    toast.success('Договор создан')
  }

  const updateContract = async (contractId, contractAttrs) => {
    await api.patch(
      `/api/v1/abiturient/applications/${application.value.id}/contracts/${contractId}`,
      { contract: contractAttrs }
    )
    await fetchContracts()
    toast.success('Договор обновлён')
  }

  //getters
  const isBachelor = computed(() => application.value?.education_level === 'bachelor')
  const isMagistrant = computed(() => application.value?.education_level === 'magistrant')
  const isAspirant = computed(() => application.value?.education_level === 'aspirant')

  return {
    application, entranceExamDisciplines, entranceExamErrors, scanCategories, loading,
    fetchForAbiturient, fetchForSecretary, uploadScan, destroyScan, updateEntranceExam, clearEntranceExamError,
    getChangeHistory,

    //contracts
    contracts, payContestGroups, contractInfoKinds, contractCustomerKinds,
    contractTypes, contractPaymentTypeKinds, paymentTypes,
    fetchContracts, notifyContract, createContract, updateContract,

    //getters
    isBachelor, isMagistrant, isAspirant
  }
})
